import { loadAllAssets } from "./loader";
import { TextShader } from "./rendering/TextShader";
import {
  DEFAULT_STATE,
  InstantState,
  Sequencer,
  ShaderName,
} from "./sequencer";
import { debounce, throttle } from "./util";
import { SDFQuad } from "./rendering/scenes/SDFQuad";
import {
  createBloomPipeline,
  createPostEffectsPipeline,
} from "./rendering/createPostEffectsPipeline";
import { MainShader as MainShader } from "./rendering/scenes/MainShader";
import { IntroShader } from "./rendering/scenes/IntroShader";
import { timeline } from "./scenes/timeline";
import { MacaroniFactoryShader } from "./rendering/scenes/MacaroniFactoryShader";
import { OutroShader } from "./rendering/scenes/OutroShader";
import { MacaroniMachineShader } from "./rendering/scenes/MachineShader";
import { MacaroniBoxShader } from "./rendering/scenes/MacaroniBoxShader";
import { PostEffectsPipeline } from "./rendering/PostEffectsPipeline";
import { TesseractShader } from "./rendering/scenes/TesseractShader";
import { LasagaShader } from "./rendering/scenes/LasagaShader";

const canvas = document.getElementById("demo") as HTMLCanvasElement;
const gl = canvas.getContext("webgl2", { powerPreference: "high-performance" });

const loader = loaderState();

let loaderText: HTMLElement | undefined = document.getElementById("loader")!;
let statusText: HTMLElement | undefined =
  document.getElementById("status-text")!;
let disclaimer: HTMLElement | undefined =
  document.getElementById("disclaimer")!;
let log: HTMLElement | undefined = document.getElementById("loader-log")!;
let loaderFarjan: HTMLElement | undefined =
  document.getElementById("loader-farjan")!;

function loaderState(): {
  graphics: number;
  assets: number;
  audio: number;
  readonly totalProgress: number;
} {
  const ASSETS_WEIGHT = 0.3;
  const AUDIO_WEIGHT = 0.2;
  const GRAPHICS_WEIGHT = 0.5;

  let graphics = 0;
  let assets = 0;
  let audio = 0;

  return {
    get graphics() {
      return graphics;
    },
    set graphics(progress: number) {
      graphics = progress;
      updateLoadProgress();
    },
    get assets() {
      return assets;
    },
    set assets(progress: number) {
      assets = progress;
      updateLoadProgress();
    },
    get audio() {
      return audio;
    },
    set audio(progress: number) {
      audio = progress;
      updateLoadProgress();
    },
    get totalProgress() {
      return (
        graphics * GRAPHICS_WEIGHT +
        assets * ASSETS_WEIGHT +
        audio * AUDIO_WEIGHT
      );
    },
  };
}

function addLogMessage(message: string) {
  // Prepend the message to the log as a new span element
  const span = document.createElement("span");
  span.innerText = message;
  log?.prepend(span);
}

function updateLoadProgress() {
  const totalProgress = loader.totalProgress;
  if (statusText) {
    if (totalProgress >= 1.0) {
      statusText.innerText = "Press F for fullscreen, click to start.";
    } else {
      statusText.innerText = (totalProgress * 100).toFixed(0) + "%";
    }
  }

  document.documentElement.style.setProperty(
    "--progress",
    (totalProgress * 100).toString() + "%",
  );
}

if (!gl) {
  throw new Error("WebGL not supported");
}

const colorBufferFloatExt = gl.getExtension("EXT_color_buffer_float");
if (!colorBufferFloatExt) {
  throw new Error("EXT_color_buffer_float is not supported");
}

function savePositionToUrl(now: number) {
  const url = new URL(window.location.href);
  url.searchParams.set("t", now.toString());
  window.history.replaceState({}, "", url.toString());
}

const debouncedSavePositionToUrl = debounce(savePositionToUrl, 100);

const url = new URL(window.location.href);

const debugMode = url.searchParams.get("debug");
const hidpiMode = url.searchParams.get("hidpi");

let debug: HTMLElement | undefined;

if (!debugMode) {
  // Add .production class to body to hide debug elements
  document.body.classList.add("production");
}

async function init(gl: WebGL2RenderingContext) {
  // Create audio tag for music
  const makaronUrl = new URL("MakaronMemoir.ogg", import.meta.url);
  const audioElement = document.createElement("audio");
  audioElement.src = makaronUrl.href;
  audioElement.loop = false;
  audioElement.autoplay = false;
  audioElement.controls = Boolean(debugMode);
  document.body.appendChild(audioElement);

  audioElement.addEventListener("canplay", () => {
    addLogMessage("Audio ready to play!");
    loader.audio = 1;
  });

  // Restore position from URL
  const t = url.searchParams.get("t");
  if (t) {
    audioElement.currentTime = parseFloat(t);
  }

  if (debugMode) {
    // Create debug info box
    debug = document.createElement("div");
    debug.id = "debug";
    document.body.appendChild(debug);

    // Delete instruction text and loader
    loaderText?.remove();
    loaderText = undefined;
    statusText?.remove();
    statusText = undefined;
    disclaimer?.remove();
    disclaimer = undefined;
  }

  const audioContext = new AudioContext();
  const mediaElement = audioContext.createMediaElementSource(audioElement);
  mediaElement.connect(audioContext.destination);

  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 32;
  const bufferLength = analyser.frequencyBinCount;
  const fftArray = new Float32Array(bufferLength);
  mediaElement.connect(analyser);

  function updateFft() {
    analyser.getFloatFrequencyData(fftArray);
  }

  function createFftVisualizer() {
    const canvas = document.createElement("canvas");
    canvas.width = bufferLength * 16;
    canvas.height = 100;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d")!;

    function render() {
      requestAnimationFrame(render);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "white";
      for (let i = 0; i < bufferLength; i++) {
        const value = fftArray[i];
        ctx.fillRect(i * 16, 0, 16, value + 100);
      }
    }

    render();
  }

  if (debugMode) {
    createFftVisualizer();
  }

  addLogMessage("Loading assets...");

  const assets = await loadAllAssets(gl, (progress, message) => {
    addLogMessage(message);
    loader.assets = progress;
  });

  addLogMessage("Assets loaded!");

  const onResizeCallbacks: ((resolution: [number, number]) => void)[] = [];

  const textShader = TextShader.create(gl);

  let width = 0;
  let height = 0;

  function updateResolution() {
    // Calculate 16:9 resolution based on window size
    const ratio = 16 / 9;
    const windowRatio = window.innerWidth / window.innerHeight;
    let canvasWidth = window.innerWidth;
    let canvasHeight = window.innerHeight;

    if (windowRatio > ratio) {
      canvasWidth = window.innerHeight * ratio;
    } else {
      canvasHeight = window.innerWidth / ratio;
    }

    canvas.style.width = canvasWidth + "px";
    canvas.style.height = canvasHeight + "px";

    width = hidpiMode
      ? Math.floor(canvasWidth * window.devicePixelRatio)
      : Math.floor(canvasWidth);
    height = hidpiMode
      ? Math.floor(canvasHeight * window.devicePixelRatio)
      : Math.floor(canvasHeight);

    canvas.width = width;
    canvas.height = height;
    onResizeCallbacks.forEach((cb) => cb([width, height]));
  }

  updateResolution();

  addLogMessage("Creating post effects pipeline...");

  const bloomPipeline = createBloomPipeline(gl, [width, height]);
  onResizeCallbacks.push((resolution) => {
    bloomPipeline.resize(resolution);
  });

  const postEffectsPipeline = createPostEffectsPipeline(
    gl,
    [width, height],
    bloomPipeline.getScreenBuffer(),
  );
  onResizeCallbacks.push((resolution) => {
    postEffectsPipeline.resize(resolution);
  });

  addLogMessage("Post effects pipeline created!");

  const debouncedUpdateResolution = debounce(updateResolution, 100);

  window.addEventListener("resize", () => {
    debouncedUpdateResolution();
  });

  const shaderCreators: Record<ShaderName, () => SDFQuad> = {
    intro: () => new IntroShader(gl, assets),
    main: () => new MainShader(gl, assets),
    macaroniFactory: () => new MacaroniFactoryShader(gl, assets),
    outro: () => new OutroShader(gl, assets),
    macaroniMachine: () => new MacaroniMachineShader(gl, assets),
    macaroniBox: () => new MacaroniBoxShader(gl, assets),
    tesseract: () => new TesseractShader(gl, assets),
    lasaga: () => new LasagaShader(gl, assets),
  };
  const TOTAL_SHADERS = Object.keys(shaderCreators).length;
  const TOTAL_GL_LOADING_STEPS = 2 + TOTAL_SHADERS;

  // 1: Setup GL
  // 2 to N: Compile all shaders
  // N + 1: Warm up all shaders
  loader.graphics += 1 / TOTAL_GL_LOADING_STEPS;

  const loadedShaders: Partial<Record<ShaderName, SDFQuad>> = {};
  let loadedShaderIndex = 1;

  for (const [name, creator] of Object.entries(shaderCreators)) {
    addLogMessage(
      `Loading scene shader ${name}... (${loadedShaderIndex}/${TOTAL_SHADERS})`,
    );
    const shader = await runImmediately(creator);
    loadedShaders[name as ShaderName] = shader;
    loader.graphics += 1 / TOTAL_GL_LOADING_STEPS;
    loadedShaderIndex++;
  }

  addLogMessage("Scene shaders loaded!");
  const shaderByName = loadedShaders as Record<ShaderName, SDFQuad>;

  // Do not warmup in debug mode
  if (!debugMode) {
    addLogMessage("Warming up shaders... this may take a while.");
    let warmupIndex = 0;

    for (const shader of Object.values(shaderByName)) {
      await runImmediately(() => {
        renderOnceWithState({
          shader,
          state: DEFAULT_STATE,
          fftArray,
          gl,
          postEffectsPipeline,
          bloomPipeline,
          width,
          height,
        });
      });

      warmupIndex++;
      addLogMessage(`Warming up shaders... ${warmupIndex}/6`);
    }
  }

  loader.graphics += 1 / TOTAL_GL_LOADING_STEPS;

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.enable(gl.DEPTH_TEST);

  let sequencer = new Sequencer({
    bpm: 172,
    initialScene: timeline,
  });

  sequencer.preprocess(textShader, assets.fonts);

  let running = false;
  let startTime = 0;

  const frameLength = 1000 / 60;
  let previousTime = 0;

  function render(timestamp: number, once?: boolean) {
    if (!gl) {
      return;
    }

    if (!once && timestamp - previousTime < frameLength) {
      requestAnimationFrame((timestamp) => render(timestamp));
      return;
    }

    if (!running && !once) {
      return;
    }

    updateFft();

    const musicNow = audioElement.currentTime;
    const now = running ? performance.now() / 1000 - startTime : musicNow;

    const state = sequencer.getStateAt(now);

    if (debugMode) {
      updateDebugState(state);
    }

    const shader = shaderByName[state.shader];

    renderOnceWithState({
      gl,
      shader,
      state,
      width,
      height,
      fftArray,
      postEffectsPipeline,
      bloomPipeline,
    });

    if (!once) {
      previousTime = timestamp;
      requestAnimationFrame((timestamp) => render(timestamp));
    }
  }

  const renderOnceThrottled = throttle(() => render(0, true), 100);

  function onChangeTime() {
    const musicNow = audioElement.currentTime;
    const now = performance.now() / 1000;
    startTime = now - musicNow;
  }

  render(0, true);

  addLogMessage("Ready to render!");
  canvas.classList.remove("hidden");
  loaderFarjan?.remove();
  loaderFarjan = undefined;

  if (log) {
    log.remove();
    log = undefined;
  }

  async function start() {
    if (running) {
      return;
    }
    running = true;

    if (loaderText) {
      loaderText.remove();
      loaderText = undefined;
      statusText = undefined;
      disclaimer?.remove();
      disclaimer = undefined;
    }

    await audioContext.resume();
    await audioElement.play();
    onChangeTime();
    requestAnimationFrame(() => render(0, false));
  }

  audioElement.addEventListener("play", start);
  audioElement.addEventListener("seeking", () => {
    onChangeTime();
    debouncedSavePositionToUrl(audioElement.currentTime);
    if (!running) {
      renderOnceThrottled();
    }
  });
  audioElement.addEventListener("pause", () => {
    running = false;
  });

  canvas.addEventListener("click", () => {
    if (audioElement.paused) {
      audioElement.play();
    } else {
      audioElement.pause();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "e") {
      // advance one frame
      audioElement.currentTime += 1 / 60;
    }

    if (e.key === "q") {
      // rewind one frame
      audioElement.currentTime -= 1 / 60;
    }

    if (e.key === "r") {
      // reset
      audioElement.currentTime = 0;
    }

    if (e.key === "f") {
      // toggle fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.body.requestFullscreen();
      }
    }
  });
}

init(gl).catch((e) => {
  console.error(e);
});

function updateDebugState(state: InstantState) {
  const {
    now,
    sceneNow,
    nowInSceneBeats,
    name,
    beat,
    sceneId,
    camera,
    sceneProgress,
    sceneVariables,
    sceneVariables2,
    fade,
    backgroundColor,
    lighting,
    shader,
    effects,
  } = state;
  debug!.innerText = JSON.stringify(
    {
      now,
      sceneNow,
      nowInSceneBeats,
      name,
      beat,
      sceneId,
      camera,
      sceneProgress,
      sceneVariables,
      sceneVariables2,
      fade,
      backgroundColor,
      lighting,
      shader,
      effects,
    },
    null,
    2,
  );
}

function renderOnceWithState({
  shader,
  state,
  width,
  height,
  fftArray,
  postEffectsPipeline,
  bloomPipeline,
  gl,
}: {
  shader: SDFQuad;
  state: InstantState;
  width: number;
  height: number;
  fftArray: Float32Array;
  postEffectsPipeline: PostEffectsPipeline;
  bloomPipeline: PostEffectsPipeline;
  gl: WebGL2RenderingContext;
}) {
  gl.viewport(0, 0, width, height);
  gl.disable(gl.BLEND);

  // Clear screen
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

  postEffectsPipeline.beforeScene();

  // Clear buffer
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  gl.depthFunc(gl.LEQUAL);

  shader.render({
    ...state,
    resolution: [width, height],
    fft: fftArray,
  });

  bloomPipeline.copyFromScreenBuffer();

  bloomPipeline.render({
    now: state.now,
    resolution: [width, height],
    fadeColor: [0, 0, 0, 0],
  });

  postEffectsPipeline.render({
    now: state.now,
    resolution: [width, height],
    fadeColor: state.fade,
    enableEffects:
      state.effects?.enable?.[0] === "vhs"
        ? {
            "VHS Distortion": true,
          }
        : undefined,
  });

  gl.depthFunc(gl.ALWAYS);
  gl.enable(gl.BLEND);

  for (const text of state.texts) {
    text.text.render({
      now: state.now,
      resolution: [width, height],
      opacity: text.opacity,
      params: text.params,
    });
  }
}

// Runs a blocking function but allows the event loop to run in between
async function runImmediately<T>(cb: () => T): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(cb());
    }, 0);
  });
}
